import Seo from "@/components/seo/Seo";
import { HelpRequestButton } from "@/components/HelpRequestButton";
import { ShareKakaoButton } from "@/components/ShareKakaoButton";
import { LocationSelector } from "@/components/LocationSelector";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useTopicLocale } from "@/hooks/use-topic-locale";
import { useRegion } from "@/contexts/RegionContext";
import { toArray } from "@/lib/array";
import { loadLegacyTopicContent } from "@/lib/legacy-topic-guides";
import {
  businessCategories,
  communityTalkCategories,
  meetingCategories,
  type Article,
  type CommunityTalk,
  type Meeting,
} from "@shared/schema";
import {
  buildTopicConnectionSummary,
  getRelatedTopicPosts,
  type TopicConnectionKey,
} from "@shared/topic-related-content";
import {
  getPriorityTopicContentEntry,
  type TopicContentManifestEntry,
} from "@shared/topic-content-manifest";
import { getTopicContentSources } from "@shared/topic-content-sources";
import { getTopicGuide } from "@shared/topic-guides";
import {
  getTopicProblem,
  getTopicProblemUrgencyLabel,
} from "@shared/topic-problems";
import {
  evaluateTopicContentGate,
  type TopicContentGateDecision,
  type TopicContentSection,
} from "@shared/topic-content-gates";
import { getTopicContentDisclaimerText } from "@shared/topic-content-disclaimer";
import { getTopicBaselineGuide } from "@shared/topic-baseline-guide";
import { getTopicHelpConnection } from "@shared/topic-help-connection";
import {
  getBusinessPlacementLevel,
  rankBusinessPlacements,
} from "@shared/business-placement";
import { getCanonicalSuburbName } from "@shared/suburbs";
import {
  getTopicCategoryLabel,
  getTopicDescription,
  getTopicById,
  getTopicLabel,
  getTopicSecondaryLabel,
  getTopicStageDescription,
  getTopicStageLabel,
  getTopicStages,
  TOPIC_CATEGORIES,
} from "@shared/topic-taxonomy";
import { topicPostMetadataToSearch } from "@shared/topic-post-metadata";
import { useMutation, useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { enUS, ko } from "date-fns/locale";
import {
  ArrowLeft,
  ArrowRight,
  AlertTriangle,
  Bookmark,
  BookmarkCheck,
  Building2,
  CalendarDays,
  CheckCircle2,
  Clock,
  FileText,
  HelpCircle,
  ExternalLink,
  MapPin,
  MessageSquareText,
  Phone,
  ShieldCheck,
  Star,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Link, useRoute } from "wouter";

type TopicBusiness = {
  id: string;
  businessName?: string | null;
  businessNameKo?: string | null;
  name?: string | null;
  category?: string | null;
  categoryLabel?: string | null;
  state?: string | null;
  city?: string | null;
  suburb?: string | null;
  description?: string | null;
  shortDescription?: string | null;
  contactPhone?: string | null;
  phone?: string | null;
  isVerified?: boolean | null;
  membershipTier?: string | null;
  subscriptionStatus?: string | null;
  hasActiveAdvertisement?: boolean | null;
  averageRating?: string | number | null;
  reviewCount?: number | null;
  views?: number | null;
  createdAt?: string | Date | null;
  updatedAt?: string | Date | null;
};

type ContentStatusSection = {
  section: TopicContentSection;
  title: string;
  description: string;
  decision: TopicContentGateDecision;
  icon: typeof FileText;
};

function buildContentSections(
  entry: TopicContentManifestEntry,
  locale: "ko" | "en",
): ContentStatusSection[] {
  const isKorean = locale === "ko";

  return [
    {
      section: "ai_summary",
      title: isKorean ? "핵심 요약" : "Key Summary",
      description: isKorean
        ? "공식 자료를 바탕으로 확인한 핵심 내용을 준비하고 있습니다."
        : "We are preparing a clear summary based on trusted official information.",
      decision: evaluateTopicContentGate(entry, "ai_summary", locale),
      icon: ShieldCheck,
    },
    {
      section: "life_info",
      title: isKorean ? "생활 안내" : "Practical Guide",
      description: isKorean
        ? "실제 생활에 필요한 순서와 준비사항을 알기 쉽게 정리합니다."
        : "Clear steps and practical details for everyday life in Australia.",
      decision: evaluateTopicContentGate(entry, "life_info", locale),
      icon: FileText,
    },
    {
      section: "faq",
      title: isKorean ? "자주 묻는 질문" : "FAQ",
      description: isKorean
        ? "많이 궁금해하는 질문을 정확한 최신 정보로 정리합니다."
        : "Helpful answers to common questions, checked for accuracy and freshness.",
      decision: evaluateTopicContentGate(entry, "faq", locale),
      icon: MessageSquareText,
    },
    {
      section: "sources",
      title: isKorean ? "확인한 공식 자료" : "Trusted Sources",
      description: isKorean
        ? "정부기관과 공신력 있는 기관의 자료를 확인해 연결합니다."
        : "Links to official government and trusted organisation resources.",
      decision: evaluateTopicContentGate(entry, "sources", locale),
      icon: ShieldCheck,
    },
  ];
}

function TopicContentGateCard({
  section,
  isKorean,
}: {
  section: ContentStatusSection;
  isKorean: boolean;
}) {
  const Icon = section.icon;
  const tone = getGateTone(section.decision);

  return (
    <article className="rounded-lg border border-border p-4">
      <div className="flex items-start justify-between gap-3">
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${tone.iconClass}`}
        >
          <Icon className="h-4 w-4" />
        </span>
        <Badge variant={tone.badgeVariant} className="rounded-md">
          {getGateLabel(section.decision, isKorean)}
        </Badge>
      </div>
      <h3 className="mt-4 text-base font-semibold text-foreground">
        {section.title}
      </h3>
      <p className="mt-2 text-sm leading-6 text-muted-foreground">
        {section.description}
      </p>
      <p className="mt-3 text-xs font-medium text-muted-foreground">
        {getGateReasonText(section.decision, isKorean)}
      </p>
    </article>
  );
}

function getGateTone(decision: TopicContentGateDecision): {
  iconClass: string;
  badgeVariant: "default" | "secondary" | "outline" | "destructive";
} {
  if (decision.allowed && decision.reason === "allowed") {
    return {
      iconClass: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
      badgeVariant: "secondary",
    };
  }

  if (decision.allowed) {
    return {
      iconClass: "bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300",
      badgeVariant: "outline",
    };
  }

  return {
    iconClass: "bg-muted text-muted-foreground",
    badgeVariant: "outline",
  };
}

function getGateLabel(
  decision: TopicContentGateDecision,
  isKorean: boolean,
): string {
  if (decision.allowed && decision.reason === "allowed") {
    return isKorean ? "이용 가능" : "Available";
  }
  if (decision.allowed && decision.reason === "source_panel_shell_only") {
    return isKorean ? "자료 준비 중" : "Sources coming soon";
  }
  if (decision.allowed && decision.reason === "korean_only") {
    return isKorean ? "한국어 공개" : "Korean only";
  }
  return isKorean ? "준비 중" : "Coming soon";
}

function getGateReasonText(
  decision: TopicContentGateDecision,
  isKorean: boolean,
): string {
  const koText: Record<TopicContentGateDecision["reason"], string> = {
    allowed: "확인된 최신 내용을 안내하고 있습니다.",
    korean_only: "한국어 안내를 먼저 제공하며 영어 안내도 준비하고 있습니다.",
    source_panel_shell_only:
      "정확한 공식 자료를 확인하고 있습니다.",
    not_reviewed: "정확한 안내를 위해 공식 자료를 확인하고 내용을 다듬고 있습니다.",
    not_bilingual_reviewed:
      "자연스럽고 정확한 영어 안내를 준비하고 있습니다.",
    stale_high_risk:
      "중요 정보가 바뀌었을 수 있어 최신 내용을 다시 확인하고 있습니다.",
    stale_low_risk: "최근 변경사항을 확인한 뒤 내용을 업데이트할 예정입니다.",
    requires_real_data:
      "관련 질문과 업체, 모임이 생기면 이곳에서 바로 연결해 드립니다.",
  };
  const enText: Record<TopicContentGateDecision["reason"], string> = {
    allowed: "Clear, up-to-date information is available here.",
    korean_only:
      "Korean guidance can be shown, but English publication still needs review.",
    source_panel_shell_only:
      "We are checking the most useful official sources.",
    not_reviewed:
      "We are checking official information and shaping it into a practical guide.",
    not_bilingual_reviewed:
      "English publication requires a separate translation and review step.",
    stale_high_risk:
      "This important information may have changed, so we are checking the latest details.",
    stale_low_risk:
      "Stale lower-risk content can only be shown with a freshness warning.",
    requires_real_data:
      "Related questions, services, experts, and meetups will appear as they become available.",
  };

  return isKorean ? koText[decision.reason] : enText[decision.reason];
}

function getContentStatusLabel(
  entry: TopicContentManifestEntry,
  isKorean: boolean,
): string {
  const labels: Record<TopicContentManifestEntry["contentStatus"], string> =
    isKorean
      ? {
          planned: "준비 예정",
          source_research: "자료 확인 중",
          source_ready: "자료 확인 완료",
          draft_ko: "안내문 작성 중",
          editorial_review: "내용 다듬는 중",
          specialist_review: "전문가 확인 중",
          approved_ko: "한국어 안내 준비됨",
          english_summary: "영어 안내 작성 중",
          bilingual_review: "영문 확인 중",
          published: "이용 가능",
          review_soon: "업데이트 예정",
          stale: "업데이트 중",
          archived: "현재 제공하지 않음",
        }
      : {
          planned: "Coming up",
          source_research: "Checking information",
          source_ready: "Information checked",
          draft_ko: "Writing the guide",
          editorial_review: "Polishing the guide",
          specialist_review: "Expert check",
          approved_ko: "Korean guide ready",
          english_summary: "Writing English guide",
          bilingual_review: "Checking English guide",
          published: "Available",
          review_soon: "Update coming",
          stale: "Being updated",
          archived: "Currently unavailable",
        };

  return labels[entry.contentStatus];
}

function getContentCareLabel(
  entry: TopicContentManifestEntry,
  isKorean: boolean,
): string {
  const labels: Record<TopicContentManifestEntry["riskBand"], [string, string]> = {
    "R1-general": ["생활정보", "Everyday guide"],
    "R2-regulated": ["정확성 확인 필요", "Carefully checked"],
    "R3-high-risk": ["전문가 확인 필요", "Expert checked"],
    "R4-critical": ["중요 정보", "Important guidance"],
  };
  return labels[entry.riskBand][isKorean ? 0 : 1];
}

function getTestGuideStatusLabel(status: string): string {
  if (status === "korean_test_preview") {
    return "테스트 미리보기 · 전문가 검토 전";
  }
  if (status === "review_required") return "테스트 정보 · 추가 확인 필요";
  if (status === "verified_publish") return "공식 자료·전문가 검토 완료";
  return "테스트 생활정보";
}

function getSourceVerificationLabel(status: string): string {
  const labels: Record<string, string> = {
    live_verified: "공식 페이지 확인",
    official_index_verified: "공식 검색 정보 확인",
    official_document_verified: "공식 문서 확인",
    official_access_limited: "공식 페이지 접근 제한",
    supporting_source_only: "보조 자료",
  };
  return labels[status] ?? "확인 경로 제공";
}

export default function TopicDetail() {
  const { locale, setLocale, isKorean } = useTopicLocale();
  const {
    selectedRegion,
    selectedSuburb,
    isLoading: isRegionLoading,
    locationDialogOpen,
    setLocationDialogOpen,
  } = useRegion();
  const localeKey = isKorean ? "ko" : "en";
  const [, params] = useRoute<{ topicId: string }>("/topics/:topicId");
  const topicId =
    params && typeof params.topicId === "string" ? params.topicId : "";
  const topic = getTopicById(topicId);
  const topicHelpConnection = getTopicHelpConnection(topicId);
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();

  // "저장" (다음 행동, 4장 Topic 표준 구조) - reuses the same bookmarks table/API
  // already wired up for article/video/product/business (functions/api/bookmarks).
  const { data: topicBookmarkData } = useQuery<{ isBookmarked: boolean; bookmarkId?: string }>({
    queryKey: ["/api/bookmarks/check", user?.uid, "topic", topicId],
    queryFn: async () => {
      const params = new URLSearchParams({
        userId: user?.uid || "",
        itemType: "topic",
        itemId: topicId,
      });
      const res = await fetch(`/api/bookmarks/check?${params.toString()}`);
      if (!res.ok) throw new Error("Failed to check bookmark");
      return res.json();
    },
    enabled: isAuthenticated && !!user && Boolean(topicId),
  });
  const isTopicBookmarked = topicBookmarkData?.isBookmarked || false;

  const addTopicBookmarkMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("로그인이 필요합니다");
      return await apiRequest("POST", "/api/bookmarks", {
        userId: user.uid,
        itemType: "topic",
        itemId: topicId,
        itemTitle: topic ? getTopicLabel(topic, locale) : topicId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/bookmarks/check", user?.uid, "topic", topicId] });
      queryClient.invalidateQueries({ queryKey: ["/api/bookmarks", user?.uid] });
      toast({ title: "저장했습니다", description: "마이페이지에서 확인하실 수 있습니다" });
    },
    onError: (error: any) => {
      toast({ title: "저장 실패", description: error.message, variant: "destructive" });
    },
  });

  const removeTopicBookmarkMutation = useMutation({
    mutationFn: async () => {
      if (!topicBookmarkData?.bookmarkId) throw new Error("저장 항목을 찾을 수 없습니다");
      return await apiRequest("DELETE", `/api/bookmarks/${topicBookmarkData.bookmarkId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/bookmarks/check", user?.uid, "topic", topicId] });
      queryClient.invalidateQueries({ queryKey: ["/api/bookmarks", user?.uid] });
      toast({ title: "저장 취소", description: "저장이 해제되었습니다" });
    },
    onError: (error: any) => {
      toast({ title: "저장 취소 실패", description: error.message, variant: "destructive" });
    },
  });

  const handleTopicBookmarkToggle = () => {
    if (!isAuthenticated || !user) {
      toast({ title: "로그인이 필요합니다", variant: "destructive" });
      return;
    }
    if (isTopicBookmarked) {
      removeTopicBookmarkMutation.mutate();
    } else {
      addTopicBookmarkMutation.mutate();
    }
  };
  const isTopicBookmarkPending =
    addTopicBookmarkMutation.isPending || removeTopicBookmarkMutation.isPending;

  const hasBusinessLocation =
    !isRegionLoading && Boolean(selectedRegion && selectedRegion !== "ALL");
  const selectedBusinessSuburb = getCanonicalSuburbName(
    selectedRegion,
    selectedSuburb,
  );
  const businessQueryParams = new URLSearchParams({
    limit: "100",
    ...(topic?.businessCategoryId
      ? { category: topic.businessCategoryId }
      : {}),
  });
  if (hasBusinessLocation) businessQueryParams.set("state", selectedRegion);
  if (hasBusinessLocation && selectedBusinessSuburb) {
    businessQueryParams.set("suburb", selectedBusinessSuburb);
  }
  const relatedBusinessQueryUrl = `/api/business-directory?${businessQueryParams.toString()}`;
  const runtimeTopicGuide = getTopicGuide(topicId, localeKey);
  const { data: rawTalkPosts, isLoading: isRelatedTalkLoading } = useQuery({
    queryKey: ["/api/community-talk?limit=100"],
    enabled: Boolean(topic),
  });
  const { data: rawArticles, isLoading: isRelatedNewsLoading } = useQuery({
    queryKey: ["/api/articles?limit=100&locale=ko"],
    enabled: Boolean(topic),
  });
  const { data: rawBusinesses, isLoading: isRelatedBusinessesLoading } =
    useQuery({
      queryKey: [relatedBusinessQueryUrl],
      enabled: Boolean(topic && hasBusinessLocation),
    });
  const { data: rawMeetings, isLoading: isRelatedMeetingsLoading } = useQuery({
    queryKey: ["/api/meetings?limit=100"],
    enabled: Boolean(topic),
  });
  const {
    data: legacyTopicContent,
    isLoading: isLegacyTopicContentLoading,
  } = useQuery({
    queryKey: ["legacy-topic-guide", topicId],
    queryFn: () => loadLegacyTopicContent(topicId),
    enabled: Boolean(topic && isKorean && !runtimeTopicGuide),
    staleTime: Number.POSITIVE_INFINITY,
  });

  if (!topic) {
    return (
      <>
        <Seo
          title={
            isKorean ? "주제를 찾을 수 없습니다 — OZK" : "Topic not found — OZK"
          }
          description={
            isKorean
              ? "요청한 생활 주제를 찾을 수 없습니다. OZK 홈이나 검색에서 다시 찾아보세요."
              : "The requested life topic could not be found. Go back to OZK topics or search."
          }
          path={`/topics/${topicId}`}
        />
        <main className="min-h-screen bg-background">
          <section className="mx-auto flex min-h-[60vh] max-w-3xl flex-col items-center justify-center px-4 py-16 text-center">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <HelpCircle className="h-6 w-6 text-muted-foreground" />
            </div>
            <h1 className="text-2xl font-bold text-foreground">
              {isKorean ? "주제를 찾을 수 없습니다" : "Topic not found"}
            </h1>
            <p className="mt-3 max-w-xl text-sm leading-6 text-muted-foreground">
              {isKorean
                ? "요청한 생활 주제를 찾을 수 없습니다. 호주생활 목록에서 다시 선택하거나 검색해 보세요."
                : "We could not find that life guide. Choose another Hot Topic or try search."}
            </p>
            <div className="mt-6 flex flex-col gap-2 sm:flex-row">
              <Link href="/topics">
                <Button>{isKorean ? "호주생활로 돌아가기" : "View Hot Topics"}</Button>
              </Link>
              <Link href="/search">
                <Button variant="outline">
                  {isKorean ? "검색으로 이동" : "Go to search"}
                </Button>
              </Link>
            </div>
          </section>
        </main>
      </>
    );
  }

  const category = TOPIC_CATEGORIES.find((item) => item.id === topic.category);
  const stages = getTopicStages(topic.id);
  const contentEntry = getPriorityTopicContentEntry(topic.id);
  const questionCreateHref = `/questions/new${topicPostMetadataToSearch({
    topicId: topic.id,
    contentIntent: "question",
  })}`;
  const experienceCreateHref = `/experiences/new${topicPostMetadataToSearch({
    topicId: topic.id,
    contentIntent: "experience",
  })}`;
  const relatedBusinessParams = new URLSearchParams({
    category: topic.businessCategoryId,
  });
  if (hasBusinessLocation) relatedBusinessParams.set("region", selectedRegion);
  if (hasBusinessLocation && selectedBusinessSuburb) {
    relatedBusinessParams.set("suburb", selectedBusinessSuburb);
  }
  const relatedBusinessHref = `/business-directory?${relatedBusinessParams.toString()}`;
  const relatedTalkPosts = getRelatedTopicPosts(
    topic,
    toArray<CommunityTalk>(rawTalkPosts),
    5,
  );
  const relatedArticles = getRelatedTopicPosts(
    topic,
    toArray<Article>(rawArticles),
    4,
    { minimumKeywordScore: 6 },
  );
  const businessesWithCategoryLabels = toArray<TopicBusiness>(rawBusinesses).map(
    (business) => ({
      ...business,
      categoryLabel:
        business.categoryLabel ||
        businessCategories.find((item) => item.id === business.category)?.label,
    }),
  );
  const topicMatchedBusinesses = getRelatedTopicPosts(
    topic,
    businessesWithCategoryLabels,
    businessesWithCategoryLabels.length,
  );
  const relatedBusinesses = rankBusinessPlacements(topicMatchedBusinesses).slice(0, 4);
  const meetingsWithCategoryLabels = toArray<Meeting>(rawMeetings).map(
    (meeting) => ({
      ...meeting,
      categoryLabel:
        meetingCategories.find((item) => item.id === meeting.category)?.label ||
        meeting.category,
    }),
  );
  const relatedMeetings = getRelatedTopicPosts(
    topic,
    meetingsWithCategoryLabels,
    4,
  );
  const connectionSummary = buildTopicConnectionSummary({
    stages: stages.length,
    questions: relatedTalkPosts.length,
    news: relatedArticles.length,
    businesses: relatedBusinesses.length,
    meetings: relatedMeetings.length,
  });
  const isConnectionSummaryLoading =
    isRelatedTalkLoading ||
    isRelatedNewsLoading ||
    isRegionLoading ||
    isRelatedBusinessesLoading ||
    isRelatedMeetingsLoading;
  const dateLocale = isKorean ? ko : enUS;
  const connectionIconByKey: Record<TopicConnectionKey, typeof ArrowRight> = {
    stages: ArrowRight,
    questions: MessageSquareText,
    news: Clock,
    businesses: Building2,
    meetings: CalendarDays,
  };
  const topicGuide =
    runtimeTopicGuide ?? (isKorean ? legacyTopicContent?.guide : undefined);
  const topicProblem = getTopicProblem(topic.id);
  const topicSources = runtimeTopicGuide
    ? getTopicContentSources(topic.id)
    : isKorean
      ? legacyTopicContent?.sources ?? []
      : [];
  const baselineGuide = getTopicBaselineGuide(topic, localeKey);
  const topicSourceNumberById = new Map(
    topicSources.map((source, index) => [source.sourceId, index + 1]),
  );
  const disclaimerText = contentEntry
    ? getTopicContentDisclaimerText(
        {
          disclaimerType: contentEntry.disclaimerType,
          lastReviewed: topicGuide?.checkedAt ?? (isKorean ? "업데이트 준비 중" : "Update in progress"),
          jurisdictions: contentEntry.jurisdictions,
          sourceTrustLabel: topicGuide
            ? isKorean
              ? "호주 정부 공식 자료 확인"
              : "Australian Government sources checked"
            : isKorean
              ? "공식 자료 확인 중"
              : "Official information being checked",
        },
        localeKey,
      )
    : null;

  return (
    <>
      <Seo
        title={`${runtimeTopicGuide?.title || getTopicLabel(topic, locale)} — OZK`}
        description={runtimeTopicGuide?.summary || getTopicDescription(topic, locale)}
        path={`/topics/${topic.id}`}
        keywords={[topic.label, topic.labelEn, ...topic.keywords]}
      />
      <LocationSelector
        open={locationDialogOpen}
        onOpenChange={setLocationDialogOpen}
        className="hidden"
      />

      <main className="min-h-screen bg-background">
        <section className="mx-auto w-full max-w-[1536px] px-4 py-8 md:px-6 md:py-10 lg:px-8 lg:py-12">
          <div className="mb-6">
            <Link href="/topics">
              <Button variant="ghost" size="sm" className="-ml-2 gap-2">
                <ArrowLeft className="h-4 w-4" />
                {isKorean ? "호주생활" : "Hot Topics"}
              </Button>
            </Link>
          </div>

          <div className="grid gap-8 lg:grid-cols-[1fr_320px] lg:items-start">
            <div className="space-y-8">
              <header className="rounded-lg border border-border bg-card p-5 md:p-7">
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  {category && (
                    <Badge variant="secondary" className="rounded-md">
                      {getTopicCategoryLabel(category, locale)}
                    </Badge>
                  )}
                  <Badge variant="outline" className="rounded-md">
                    {isKorean ? "호주생활 문제 해결" : "Australian life help"}
                  </Badge>
                  {topic.requiresFreshnessCheck && (
                    <Badge variant="outline" className="rounded-md">
                      {isKorean ? "최신성 확인 필요" : "Freshness review"}
                    </Badge>
                  )}
                  {topicProblem && (
                    <Badge variant={topicProblem.urgency === "high" ? "default" : "outline"} className="rounded-md">
                      {getTopicProblemUrgencyLabel(topicProblem.urgency, localeKey)}
                    </Badge>
                  )}
                  <div className="ml-auto flex rounded-md border border-border p-1">
                    <Button
                      type="button"
                      size="sm"
                      variant={locale === "ko" ? "default" : "ghost"}
                      onClick={() => setLocale("ko")}
                      className="h-8 px-3"
                    >
                      한국어
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={locale === "en" ? "default" : "ghost"}
                      onClick={() => setLocale("en")}
                      className="h-8 px-3"
                    >
                      English
                    </Button>
                  </div>
                </div>
                <h1 className="text-3xl font-bold leading-tight text-foreground md:text-4xl">
                  {runtimeTopicGuide?.title || getTopicLabel(topic, locale)}
                </h1>
                {getTopicSecondaryLabel(topic, locale) && (
                  <p className="mt-2 text-sm font-medium text-muted-foreground">
                    {getTopicSecondaryLabel(topic, locale)}
                  </p>
                )}
                {topicProblem && (
                  <div className="mt-5 rounded-lg border border-primary/20 bg-primary/5 p-4 md:p-5">
                    <p className="text-xs font-semibold text-primary">
                      {isKorean ? "혹시 지금 이런 상황인가요?" : "Does this sound like your situation?"}
                    </p>
                    <p className="mt-2 text-lg font-bold leading-7 text-foreground">
                      {topicProblem.problem[localeKey]}
                    </p>
                    <p className="mt-3 text-sm leading-6 text-muted-foreground">
                      {topicProblem.situation[localeKey]}
                    </p>
                    <div className="mt-4 border-t border-primary/15 pt-4">
                      <p className="text-xs font-semibold text-primary">
                        {isKorean ? "지금 먼저 할 일" : "What to do first"}
                      </p>
                      <p className="mt-1 text-sm font-medium leading-6 text-foreground">
                        {topicProblem.firstAction[localeKey]}
                      </p>
                    </div>
                  </div>
                )}
                <p className="mt-5 max-w-3xl text-base leading-7 text-muted-foreground">
                  {getTopicDescription(topic, locale) ||
                    (isKorean
                      ? "이 생활 주제의 단계별 안내를 확인하세요."
                      : "Explore step-by-step guidance for this life topic.")}
                </p>
                {isKorean && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    <HelpRequestButton
                      sourceType="topic"
                      sourceId={topicId}
                      topicId={topicId}
                      zoneId={hasBusinessLocation ? selectedRegion : undefined}
                      suburb={selectedBusinessSuburb || undefined}
                      recommendationCopy={topicHelpConnection?.enabled ? topicHelpConnection.handoffCopy : undefined}
                    />
                    <ShareKakaoButton
                      path={`/topics/${topicId}`}
                      contentType="topic"
                      contentId={topicId}
                      title={getTopicLabel(topic, locale)}
                      description={getTopicDescription(topic, locale) || undefined}
                    />
                  </div>
                )}
              </header>

              <section
                aria-labelledby="topic-graph-summary-heading"
                className="rounded-lg border border-border bg-card p-5"
              >
                <div>
                  <h2
                    id="topic-graph-summary-heading"
                    className="text-xl font-bold text-foreground"
                  >
                    {isKorean ? "한눈에 보는 관련 정보" : "At a Glance"}
                  </h2>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">
                    {isKorean
                      ? "이 주제와 연결된 단계별 안내, 질문, 새 소식, 업체와 모임을 한곳에서 확인하세요."
                      : "See step-by-step help, questions, updates, local businesses, and meetups in one place."}
                  </p>
                </div>

                <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                  {connectionSummary.map((item) => {
                    const Icon = connectionIconByKey[item.key];

                    return (
                      <a
                        key={item.key}
                        href={`#${item.targetId}`}
                        className="rounded-lg border border-border p-4 transition-colors hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring"
                        aria-label={`${item.label} 섹션으로 이동`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-muted text-muted-foreground">
                            <Icon className="h-4 w-4" />
                          </span>
                          <Badge
                            variant={item.isActive ? "secondary" : "outline"}
                            className="rounded-md"
                          >
                            {item.isActive
                              ? isKorean
                                ? "연결됨"
                                : "Connected"
                              : isKorean
                                ? "준비중"
                                : "Preparing"}
                          </Badge>
                        </div>
                        <div className="mt-4">
                          {isConnectionSummaryLoading && item.key !== "stages" ? (
                            <Skeleton className="h-8 w-12 rounded-md" />
                          ) : (
                            <p className="text-2xl font-bold text-foreground">
                              {item.value}
                            </p>
                          )}
                          <p className="mt-1 text-sm font-medium text-foreground">
                            {item.label}
                          </p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            {item.description}
                          </p>
                        </div>
                      </a>
                    );
                  })}
                </div>
              </section>

              {topicGuide && (
                <section
                  id="topic-guide"
                  aria-labelledby="topic-guide-heading"
                  className="scroll-mt-24 space-y-6"
                >
                  <div className="rounded-lg border border-border bg-card p-5 md:p-7">
                    <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                      <div>
                        <Badge variant="secondary" className="rounded-md">
                          {getTestGuideStatusLabel(topicGuide.publicationStatus)}
                        </Badge>
                        <h2
                          id="topic-guide-heading"
                          className="mt-4 text-2xl font-bold text-foreground"
                        >
                          핵심 요약
                        </h2>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        적용 지역: {topicGuide.jurisdictionLabel}
                      </span>
                    </div>
                    <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/20 dark:text-amber-100">
                      이 안내는 OZK 테스트 운영을 위해 공식 자료를 바탕으로 작성되었습니다.
                      실제 신청이나 중요한 결정을 하기 전에 연결된 공식 기관에서 최신 내용을
                      다시 확인하고, 필요한 경우 등록된 전문가에게 확인하세요.
                    </div>
                    <p className="mt-4 max-w-4xl text-base leading-8 text-foreground">
                      {topicGuide.summary}
                    </p>
                    <ul className="mt-5 grid gap-3 md:grid-cols-2">
                      {topicGuide.keyPoints.map((point) => (
                        <li
                          key={point}
                          className="flex gap-3 rounded-lg bg-muted/50 p-3 text-sm leading-6 text-foreground"
                        >
                          <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-emerald-600" />
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h2 className="text-2xl font-bold text-foreground">
                        순서대로 알아보기
                      </h2>
                      <p className="mt-2 text-sm leading-6 text-muted-foreground">
                        신청 전 확인부터 발급 후 관리까지 필요한 내용을 순서대로 정리했습니다.
                      </p>
                    </div>
                    {topicGuide.sections.map((guideSection) => (
                      <article
                        key={guideSection.id}
                        className="rounded-lg border border-border bg-card p-5 md:p-6"
                      >
                        <h3 className="text-lg font-bold text-foreground">
                          {guideSection.title}
                        </h3>
                        <div className="mt-4 space-y-3">
                          {guideSection.body.map((paragraph) => (
                            <p
                              key={paragraph}
                              className="text-sm leading-7 text-muted-foreground md:text-base"
                            >
                              {paragraph}
                            </p>
                          ))}
                        </div>
                        {guideSection.checklist && (
                          <div className="mt-5 rounded-lg bg-muted/50 p-4">
                            <p className="text-sm font-semibold text-foreground">
                              준비 체크리스트
                            </p>
                            <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                              {guideSection.checklist.map((item) => (
                                <li
                                  key={item}
                                  className="flex gap-2 text-sm leading-6 text-muted-foreground"
                                >
                                  <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-primary" />
                                  {item}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        <div
                          className="mt-5 flex flex-wrap items-center gap-2 border-t border-border pt-4 text-xs text-muted-foreground"
                          aria-label="이 섹션의 공식 출처"
                        >
                          <span className="font-semibold text-foreground">공식 근거</span>
                          {guideSection.sourceIds.map((sourceId) => {
                            const source = topicSources.find(
                              (item) => item.sourceId === sourceId,
                            );
                            return source ? (
                              <a
                                key={sourceId}
                                href={`#topic-source-${sourceId}`}
                                className="rounded-md border border-border px-2 py-1 transition-colors hover:bg-accent hover:text-foreground"
                              >
                                [{topicSourceNumberById.get(sourceId)}] {source.publisher}
                              </a>
                            ) : null;
                          })}
                        </div>
                      </article>
                    ))}
                  </div>

                  <div className="rounded-lg border border-border bg-card p-5 md:p-6">
                    <h2 className="text-2xl font-bold text-foreground">
                      자주 묻는 질문
                    </h2>
                    <div className="mt-4 divide-y divide-border">
                      {topicGuide.faqs.map((faq) => (
                        <article key={faq.id} className="py-4 first:pt-0 last:pb-0">
                          <h3 className="font-semibold text-foreground">
                            {faq.question}
                          </h3>
                          <p className="mt-2 text-sm leading-7 text-muted-foreground">
                            {faq.answer}
                          </p>
                          <div
                            className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground"
                            aria-label="이 답변의 공식 출처"
                          >
                            <span className="font-semibold text-foreground">공식 근거</span>
                            {faq.sourceIds.map((sourceId) => {
                              const source = topicSources.find(
                                (item) => item.sourceId === sourceId,
                              );
                              return source ? (
                                <a
                                  key={sourceId}
                                  href={`#topic-source-${sourceId}`}
                                  className="rounded-md border border-border px-2 py-1 transition-colors hover:bg-accent hover:text-foreground"
                                >
                                  [{topicSourceNumberById.get(sourceId)}] {source.publisher}
                                </a>
                              ) : null;
                            })}
                          </div>
                        </article>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-lg border border-border bg-card p-5 md:p-6">
                    <h2 className="text-xl font-bold text-foreground">
                      확인한 공식 자료
                    </h2>
                    <p className="mt-2 text-sm leading-6 text-muted-foreground">
                      아래 호주 정부 자료를 {topicGuide.checkedAt}에 확인했습니다.
                    </p>
                    <ul className="mt-4 grid gap-3 md:grid-cols-2">
                      {topicSources.map((source) => (
                        <li
                          key={source.sourceId}
                          id={`topic-source-${source.sourceId}`}
                          className="scroll-mt-24"
                        >
                          <a
                            href={source.url}
                            target="_blank"
                            rel="noreferrer"
                            className="flex h-full items-start justify-between gap-3 rounded-lg border border-border p-4 text-sm transition-colors hover:bg-accent"
                          >
                            <span>
                              <span className="block font-semibold text-foreground">
                                {source.title}
                              </span>
                              <span className="mt-1 block text-xs text-muted-foreground">
                                {source.publisher}
                              </span>
                              <span className="mt-2 block text-xs text-muted-foreground">
                                {getSourceVerificationLabel(source.reviewStatus)}
                              </span>
                            </span>
                            <ExternalLink className="mt-0.5 h-4 w-4 shrink-0" />
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/20 dark:text-amber-100">
                    <div className="flex gap-3">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <div className="space-y-2">
                        <p className="font-semibold">중요 안내</p>
                        <p className="leading-6">{topicGuide.disclaimer}</p>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                          <span>확인일: {topicGuide.checkedAt}</span>
                          <span>적용 지역: {topicGuide.jurisdictionLabel}</span>
                          <span>공식 출처: {topicSources.length}건</span>
                          <span>
                            현재 상태: {getTestGuideStatusLabel(topicGuide.publicationStatus)}
                          </span>
                        </div>
                        <div className="pt-2">
                          <p className="text-xs font-semibold">추가 확인이 필요한 항목</p>
                          <ul className="mt-1 list-disc space-y-1 pl-4 text-xs leading-5">
                            {topicGuide.additionalChecks.map((item) => (
                              <li key={item}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              )}

              {isLegacyTopicContentLoading && !topicGuide && isKorean && (
                <section
                  aria-label="기존 Topic 가이드 불러오는 중"
                  className="rounded-lg border border-border bg-card p-5"
                >
                  <Skeleton className="h-6 w-48 rounded-md" />
                  <Skeleton className="mt-4 h-20 w-full rounded-md" />
                  <div className="mt-4 grid gap-3 sm:grid-cols-3">
                    <Skeleton className="h-28 rounded-md" />
                    <Skeleton className="h-28 rounded-md" />
                    <Skeleton className="h-28 rounded-md" />
                  </div>
                </section>
              )}

              {!topicGuide && !isLegacyTopicContentLoading && (
                <section
                  id="topic-baseline-guide"
                  aria-labelledby="topic-baseline-guide-heading"
                  className="scroll-mt-24 rounded-lg border border-border bg-card p-5"
                >
                  <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <div>
                      <h2
                        id="topic-baseline-guide-heading"
                        className="text-xl font-bold text-foreground"
                      >
                        {baselineGuide.title}
                      </h2>
                      <p className="mt-2 max-w-3xl text-sm leading-6 text-muted-foreground">
                        {baselineGuide.summary}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="secondary" className="rounded-md">
                        {baselineGuide.statusLabel}
                      </Badge>
                      {contentEntry && (
                        <Badge variant="outline" className="rounded-md">
                          {isKorean
                            ? `상태: ${getContentStatusLabel(contentEntry, true)}`
                            : `Status: ${getContentStatusLabel(contentEntry, false)}`}
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="mt-5 rounded-lg border border-primary/20 bg-primary/5 p-4">
                    <p className="text-xs font-semibold text-primary">
                      {isKorean ? "지금 먼저 할 일" : "First action"}
                    </p>
                    <p className="mt-2 text-sm font-semibold leading-6 text-foreground">
                      {baselineGuide.firstAction}
                    </p>
                  </div>

                  <div className="mt-5 grid gap-3 md:grid-cols-3">
                    {baselineGuide.steps.map((step) => (
                      <article key={step.title} className="rounded-lg border border-border p-4">
                        <h3 className="text-sm font-semibold text-foreground">
                          {step.title}
                        </h3>
                        <p className="mt-2 text-sm leading-6 text-muted-foreground">
                          {step.description}
                        </p>
                      </article>
                    ))}
                  </div>

                  <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/20 dark:text-amber-100">
                    <div className="flex gap-3">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <div className="space-y-2">
                        <p className="font-semibold">
                          {disclaimerText?.title ?? (isKorean ? "중요 안내" : "Important notice")}
                        </p>
                        <p>{baselineGuide.notice}</p>
                        {disclaimerText && (
                          <>
                            <p>{disclaimerText.body}</p>
                            <p>{disclaimerText.consult}</p>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                              <span>{disclaimerText.lastReviewedLabel}</span>
                              <span>{disclaimerText.jurisdictionLabel}</span>
                              <span>{disclaimerText.sourceTrustLabel}</span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </section>
              )}

              <section
                id="topic-stages"
                aria-labelledby="topic-stages-heading"
                className="scroll-mt-24 space-y-4"
              >
                <div>
                  <h2
                    id="topic-stages-heading"
                    className="text-2xl font-bold text-foreground"
                  >
                    {isKorean ? "단계별로 보기" : "Explore by Stage"}
                  </h2>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {isKorean
                      ? "같은 주제라도 사용자의 상황과 단계에 따라 필요한 정보가 달라집니다."
                      : "Even within the same topic, people need different information depending on their stage."}
                  </p>
                </div>

                {stages.length > 0 ? (
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {stages.map((stage, index) => (
                      <Card key={stage.id} className="rounded-lg">
                        <CardContent className="flex h-full flex-col p-4">
                          <div className="mb-3 flex h-8 w-8 items-center justify-center rounded-md bg-[#663366]/10 text-sm font-semibold text-[#663366] dark:bg-[#9966cc]/10 dark:text-[#c7a4ff]">
                            {index + 1}
                          </div>
                          <h3 className="text-base font-semibold text-foreground">
                            {getTopicStageLabel(stage, locale)}
                          </h3>
                          <p className="mt-2 text-sm leading-6 text-muted-foreground">
                            {getTopicStageDescription(stage, locale)}
                          </p>
                          <div className="mt-auto flex flex-wrap gap-2 pt-4">
                            <Link
                              href={`/questions/new${topicPostMetadataToSearch({
                                topicId: topic.id,
                                topicStageId: stage.id,
                                contentIntent: "question",
                              })}`}
                            >
                              <Button variant="outline" size="sm">
                                {isKorean ? "이 단계 질문하기" : "Ask at this stage"}
                              </Button>
                            </Link>
                            <Link
                              href={`/experiences/new${topicPostMetadataToSearch({
                                topicId: topic.id,
                                topicStageId: stage.id,
                                contentIntent: "experience",
                              })}`}
                            >
                              <Button variant="ghost" size="sm">
                                {isKorean ? "경험 공유" : "Share experience"}
                              </Button>
                            </Link>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <Card className="rounded-lg border-dashed">
                    <CardContent className="p-5 text-sm text-muted-foreground">
                      {isKorean
                        ? "이 주제의 단계별 안내를 준비하고 있습니다."
                        : "Step-by-step guidance is being prepared."}
                    </CardContent>
                  </Card>
                )}
              </section>

              <section
                id="topic-related-questions"
                aria-labelledby="topic-related-questions-heading"
                className="scroll-mt-24 rounded-lg border border-border bg-card p-5"
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h2
                      id="topic-related-questions-heading"
                      className="text-xl font-bold text-foreground"
                    >
                    {isKorean ? "관련 질문과 경험" : "Related Questions and Experiences"}
                    </h2>
                    <p className="mt-2 text-sm leading-6 text-muted-foreground">
                      {isKorean
                        ? "가이드로 해결되지 않은 질문과 이 문제를 직접 겪은 사람들의 경험입니다. 사용자 작성 내용은 공식 안내와 구분해 확인하세요."
                        : "Community questions not covered by the guide and experiences from people who faced this problem. Treat user posts separately from verified guidance."}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Link href={questionCreateHref}>
                      <Button variant="outline" size="sm">
                        {isKorean ? "질문하기" : "Ask"}
                      </Button>
                    </Link>
                    <Link href={experienceCreateHref}>
                      <Button variant="ghost" size="sm">
                        {isKorean ? "해결 경험 공유" : "Share experience"}
                      </Button>
                    </Link>
                  </div>
                </div>

                {isRelatedTalkLoading ? (
                  <div className="mt-5 space-y-3">
                    {[0, 1, 2].map((item) => (
                      <Skeleton key={item} className="h-20 w-full rounded-lg" />
                    ))}
                  </div>
                ) : relatedTalkPosts.length > 0 ? (
                  <div className="mt-5 divide-y divide-border">
                    {relatedTalkPosts.map((post) => {
                      const category = communityTalkCategories.find(
                        (item) => item.id === post.category,
                      );
                      const createdAt =
                        post.createdAt instanceof Date
                          ? post.createdAt
                          : new Date(post.createdAt ?? new Date());

                      return (
                        <Link
                          key={post.id}
                          href={`/community-talk/${post.id}`}
                          data-testid={`link-topic-related-talk-${post.id}`}
                        >
                          <article className="block py-4 first:pt-0 last:pb-0">
                            <h3 className="line-clamp-2 text-base font-semibold text-foreground">
                              {post.title}
                            </h3>
                            {post.content && (
                              <p className="mt-2 line-clamp-2 text-sm leading-6 text-muted-foreground">
                                {post.content}
                              </p>
                            )}
                            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                              <span className="inline-flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatDistanceToNow(createdAt, {
                                  addSuffix: true,
                                  locale: ko,
                                })}
                              </span>
                              {category && (
                                <>
                                  <span>·</span>
                                  <span>{category.label}</span>
                                </>
                              )}
                              {post.suburb && (
                                <>
                                  <span>·</span>
                                  <span>{post.suburb}</span>
                                </>
                              )}
                              {post.commentCount !== undefined &&
                                post.commentCount !== null &&
                                post.commentCount > 0 && (
                                  <>
                                    <span>·</span>
                                    <span>댓글 {post.commentCount}</span>
                                  </>
                                )}
                            </div>
                          </article>
                        </Link>
                      );
                    })}
                  </div>
                ) : (
                  <div className="mt-5 rounded-lg border border-dashed border-border p-5 text-sm text-muted-foreground">
                    {isKorean
                      ? "아직 관련 질문이 없습니다. 첫 질문이나 경험담을 남겨보세요."
                      : "No related questions yet. Ask the first question or share your experience."}
                  </div>
                )}
              </section>

              <section
                id="topic-related-news"
                aria-labelledby="topic-related-news-heading"
                className="scroll-mt-24 rounded-lg border border-border bg-card p-5"
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h2
                      id="topic-related-news-heading"
                      className="text-xl font-bold text-foreground"
                    >
                      {isKorean ? "관련 뉴스와 변화 신호" : "Related News and Change Signals"}
                    </h2>
                    <p className="mt-2 text-sm leading-6 text-muted-foreground">
                      {isKorean
                        ? "이 생활 주제에 영향을 줄 수 있는 뉴스와 정책 변화입니다."
                        : "News and policy changes that may affect this part of life."}
                    </p>
                  </div>
                  <Link href="/news">
                    <Button variant="outline" size="sm" className="shrink-0">
                      {isKorean ? "뉴스 보기" : "View news"}
                    </Button>
                  </Link>
                </div>

                {isRelatedNewsLoading ? (
                  <div className="mt-5 grid gap-3 sm:grid-cols-2">
                    {[0, 1, 2, 3].map((item) => (
                      <Skeleton key={item} className="h-28 w-full rounded-lg" />
                    ))}
                  </div>
                ) : relatedArticles.length > 0 ? (
                  <div className="mt-5 grid gap-3 sm:grid-cols-2">
                    {relatedArticles.map((article) => {
                      const title = isKorean
                        ? article.titleKo || article.title
                        : article.title || article.titleKo;
                      const summary = isKorean
                        ? article.summaryKo || article.summary
                        : article.summary || article.summaryKo;
                      const publishedAt = new Date(
                        article.publishedAt ??
                          article.createdAt ??
                          new Date(),
                      );

                      return (
                        <Link
                          key={article.id}
                          href={`/article/${article.id}`}
                          data-testid={`link-topic-related-article-${article.id}`}
                        >
                          <article className="h-full rounded-lg border border-border p-4 transition-colors hover:bg-accent">
                            <div className="mb-3 flex flex-wrap items-center gap-2">
                              <Badge variant="secondary" className="rounded-md">
                                {isKorean ? "변화 신호" : "Change Signal"}
                              </Badge>
                              {article.category && (
                                <Badge variant="outline" className="rounded-md">
                                  {article.category}
                                </Badge>
                              )}
                            </div>
                            <h3 className="line-clamp-2 text-base font-semibold text-foreground">
                              {title}
                            </h3>
                            {summary && (
                              <p className="mt-2 line-clamp-2 text-sm leading-6 text-muted-foreground">
                                {summary}
                              </p>
                            )}
                            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                              <span className="inline-flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatDistanceToNow(publishedAt, {
                                  addSuffix: true,
                                  locale: dateLocale,
                                })}
                              </span>
                              {article.countryOrigin && (
                                <>
                                  <span>·</span>
                                  <span>{article.countryOrigin}</span>
                                </>
                              )}
                              {article.state && (
                                <>
                                  <span>·</span>
                                  <span>{article.state}</span>
                                </>
                              )}
                            </div>
                          </article>
                        </Link>
                      );
                    })}
                  </div>
                ) : (
                  <div className="mt-5 rounded-lg border border-dashed border-border p-5 text-sm text-muted-foreground">
                    {isKorean
                      ? "아직 관련 새 소식이 없습니다. 정책이나 생활 변화가 확인되면 이곳에 알려드립니다."
                      : "No news change signals are connected to this topic yet. Relevant policy or life updates will appear here as they are collected."}
                  </div>
                )}
              </section>

              <section
                id="topic-related-businesses"
                aria-labelledby="topic-related-businesses-heading"
                className="scroll-mt-24 rounded-lg border border-border bg-card p-5"
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h2
                      id="topic-related-businesses-heading"
                      className="text-xl font-bold text-foreground"
                    >
                      {isKorean ? "관련 업체와 전문가" : "Related Businesses and Experts"}
                    </h2>
                    <p className="mt-2 text-sm leading-6 text-muted-foreground">
                      {isKorean
                        ? "이 문제를 해결하는 데 도움을 줄 수 있는 가까운 업체와 전문가입니다."
                        : "Local businesses and experts who may be able to help."}
                    </p>
                  </div>
                  <Link href={relatedBusinessHref}>
                    <Button variant="outline" size="sm" className="shrink-0">
                      {isKorean ? "업체 더 보기" : "View businesses"}
                    </Button>
                  </Link>
                </div>

                {isRegionLoading ? (
                  <div className="mt-5 grid gap-3 sm:grid-cols-2">
                    {[0, 1, 2, 3].map((item) => (
                      <Skeleton key={item} className="h-36 w-full rounded-lg" />
                    ))}
                  </div>
                ) : !hasBusinessLocation ? (
                  <div className="mt-5 rounded-lg border border-dashed border-border p-5 text-sm text-muted-foreground">
                    <p>
                      {isKorean
                        ? "로그인 위치를 설정하면 해당 지역의 관련 업체만 보여드립니다."
                        : "Set your location to see only relevant businesses in your area."}
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-4"
                      onClick={() => setLocationDialogOpen(true)}
                    >
                      {isKorean ? "위치 설정" : "Set location"}
                    </Button>
                  </div>
                ) : isRelatedBusinessesLoading ? (
                  <div className="mt-5 grid gap-3 sm:grid-cols-2">
                    {[0, 1, 2, 3].map((item) => (
                      <Skeleton key={item} className="h-36 w-full rounded-lg" />
                    ))}
                  </div>
                ) : relatedBusinesses.length > 0 ? (
                  <div className="mt-5 grid gap-3 sm:grid-cols-2">
                    {relatedBusinesses.map((business) => {
                      const name = isKorean
                        ? business.businessNameKo ||
                          business.businessName ||
                          business.name ||
                          "업체"
                        : business.businessName ||
                          business.name ||
                          business.businessNameKo ||
                          "Business";
                      const description =
                        business.shortDescription || business.description;
                      const location = [
                        business.suburb || business.city,
                        business.state,
                      ]
                        .filter(Boolean)
                        .join(", ");
                      const phone = business.contactPhone || business.phone;
                      const rating =
                        business.averageRating !== undefined &&
                        business.averageRating !== null
                          ? Number(business.averageRating)
                          : 0;
                      const hasRating = Number.isFinite(rating) && rating > 0;
                      const placementLevel = getBusinessPlacementLevel(business);
                      return (
                        <Link
                          key={business.id}
                          href={`/business-directory/${business.id}`}
                          data-testid={`link-topic-related-business-${business.id}`}
                        >
                          <article className="h-full rounded-lg border border-border p-4 transition-colors hover:bg-accent">
                            <div className="mb-3 flex flex-wrap items-center gap-2">
                              {placementLevel === "advertiser" && (
                                <Badge className="rounded-md">
                                  {isKorean ? "광고" : "Sponsored"}
                                </Badge>
                              )}
                              {placementLevel === "premium" && (
                                <Badge variant="outline" className="rounded-md">
                                  {isKorean ? "프리미엄" : "Premium"}
                                </Badge>
                              )}
                              {business.categoryLabel && (
                                <Badge variant="secondary" className="rounded-md">
                                  {business.categoryLabel}
                                </Badge>
                              )}
                              {business.isVerified && (
                                <Badge variant="outline" className="rounded-md">
                                  <CheckCircle2 className="mr-1 h-3 w-3" />
                                  {isKorean ? "인증" : "Verified"}
                                </Badge>
                              )}
                            </div>
                            <h3 className="line-clamp-2 text-base font-semibold text-foreground">
                              {name}
                            </h3>
                            {description && (
                              <p className="mt-2 line-clamp-2 text-sm leading-6 text-muted-foreground">
                                {description}
                              </p>
                            )}
                            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                              {location && (
                                <span className="inline-flex items-center gap-1">
                                  <MapPin className="h-3 w-3" />
                                  {location}
                                </span>
                              )}
                              {phone && (
                                <>
                                  {location && <span>·</span>}
                                  <span className="inline-flex items-center gap-1">
                                    <Phone className="h-3 w-3" />
                                    {phone}
                                  </span>
                                </>
                              )}
                              {hasRating && (
                                <>
                                  {(location || phone) && <span>·</span>}
                                  <span className="inline-flex items-center gap-1">
                                    <Star className="h-3 w-3" />
                                    {rating.toFixed(1)}
                                    {business.reviewCount
                                      ? ` (${business.reviewCount})`
                                      : ""}
                                  </span>
                                </>
                              )}
                            </div>
                          </article>
                        </Link>
                      );
                    })}
                  </div>
                ) : (
                  <div className="mt-5 rounded-lg border border-dashed border-border p-5 text-sm text-muted-foreground">
                    {isKorean
                      ? "아직 관련 업체가 없습니다. 도움을 줄 수 있는 업체가 확인되면 이곳에 소개합니다."
                      : "No businesses are connected to this topic yet. Relevant businesses will appear here as the directory grows."}
                  </div>
                )}
              </section>

              <section
                id="topic-related-meetings"
                aria-labelledby="topic-related-meetings-heading"
                className="scroll-mt-24 rounded-lg border border-border bg-card p-5"
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h2
                      id="topic-related-meetings-heading"
                      className="text-xl font-bold text-foreground"
                    >
                      {isKorean ? "같은 주제의 모임" : "Meetups for This Topic"}
                    </h2>
                    <p className="mt-2 text-sm leading-6 text-muted-foreground">
                      {isKorean
                        ? "같은 관심사나 생활 단계의 사람들과 연결될 수 있는 기존 모임입니다."
                        : "Meetups that can connect people with the same interest or life stage."}
                    </p>
                  </div>
                  <Link href="/meetings">
                    <Button variant="outline" size="sm" className="shrink-0">
                      {isKorean ? "모임 보기" : "View meetups"}
                    </Button>
                  </Link>
                </div>

                {isRelatedMeetingsLoading ? (
                  <div className="mt-5 grid gap-3 sm:grid-cols-2">
                    {[0, 1, 2, 3].map((item) => (
                      <Skeleton key={item} className="h-36 w-full rounded-lg" />
                    ))}
                  </div>
                ) : relatedMeetings.length > 0 ? (
                  <div className="mt-5 grid gap-3 sm:grid-cols-2">
                    {relatedMeetings.map((meeting) => {
                      const location = [
                        meeting.suburb || meeting.city,
                        meeting.state,
                      ]
                        .filter(Boolean)
                        .join(", ");
                      const nextMeetingDate = meeting.nextMeetingDate
                        ? new Date(meeting.nextMeetingDate)
                        : null;
                      const hasValidNextMeetingDate =
                        nextMeetingDate &&
                        Number.isFinite(nextMeetingDate.getTime());

                      return (
                        <Link
                          key={meeting.id}
                          href={`/meetings/${meeting.id}`}
                          data-testid={`link-topic-related-meeting-${meeting.id}`}
                        >
                          <article className="h-full rounded-lg border border-border p-4 transition-colors hover:bg-accent">
                            <div className="mb-3 flex flex-wrap items-center gap-2">
                              {meeting.categoryLabel && (
                                <Badge variant="secondary" className="rounded-md">
                                  {meeting.categoryLabel}
                                </Badge>
                              )}
                              {meeting.schedule && (
                                <Badge variant="outline" className="rounded-md">
                                  {meeting.schedule}
                                </Badge>
                              )}
                            </div>
                            <h3 className="line-clamp-2 text-base font-semibold text-foreground">
                              {meeting.title}
                            </h3>
                            {meeting.description && (
                              <p className="mt-2 line-clamp-2 text-sm leading-6 text-muted-foreground">
                                {meeting.description}
                              </p>
                            )}
                            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                              {hasValidNextMeetingDate && nextMeetingDate && (
                                <span className="inline-flex items-center gap-1">
                                  <CalendarDays className="h-3 w-3" />
                                  {formatDistanceToNow(nextMeetingDate, {
                                    addSuffix: true,
                                    locale: dateLocale,
                                  })}
                                </span>
                              )}
                              {location && (
                                <>
                                  {hasValidNextMeetingDate && <span>·</span>}
                                  <span className="inline-flex items-center gap-1">
                                    <MapPin className="h-3 w-3" />
                                    {location}
                                  </span>
                                </>
                              )}
                              {meeting.currentMembers !== undefined &&
                                meeting.currentMembers !== null &&
                                meeting.currentMembers > 0 && (
                                  <>
                                    {(hasValidNextMeetingDate || location) && (
                                      <span>·</span>
                                    )}
                                    <span>
                                      {isKorean
                                        ? `참여 ${meeting.currentMembers}`
                                        : `${meeting.currentMembers} joined`}
                                    </span>
                                  </>
                                )}
                            </div>
                          </article>
                        </Link>
                      );
                    })}
                  </div>
                ) : (
                  <div className="mt-5 rounded-lg border border-dashed border-border p-5 text-sm text-muted-foreground">
                    {isKorean
                      ? "아직 관련 모임이 없습니다. 같은 상황의 사람들과 만날 수 있는 모임이 생기면 알려드립니다."
                      : "No meetups are connected to this topic yet. Small groups for people in the same situation will appear here."}
                  </div>
                )}
              </section>
            </div>

            <aside className="space-y-4">
              <Card className="rounded-lg">
                <CardContent className="space-y-3 p-4">
                  <h2 className="text-lg font-semibold text-foreground">
                    {isKorean ? "지금 할 수 있는 일" : "What You Can Do Now"}
                  </h2>
                  <Link href={questionCreateHref}>
                    <Button className="w-full justify-between bg-[#663366] hover:bg-[#552b55]">
                      {isKorean ? "이 주제에 대해 질문하기" : "Ask about this guide"}
                      <MessageSquareText className="h-4 w-4" />
                    </Button>
                  </Link>
                  <Link href={experienceCreateHref}>
                    <Button variant="outline" className="w-full justify-between">
                      {isKorean ? "경험 또는 후기 공유하기" : "Share an experience"}
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </Link>
                  <Link href={relatedBusinessHref}>
                    <Button variant="outline" className="w-full justify-between">
                      {isKorean ? "관련 업체 찾기" : "Find related businesses"}
                      <Building2 className="h-4 w-4" />
                    </Button>
                  </Link>
                  <Link href="/meetings/new">
                    <Button variant="outline" className="w-full justify-between">
                      {isKorean ? "관련 모임 만들기" : "Create a related meetup"}
                      <CalendarDays className="h-4 w-4" />
                    </Button>
                  </Link>
                  <Button
                    variant="outline"
                    className="w-full justify-between"
                    onClick={handleTopicBookmarkToggle}
                    disabled={isTopicBookmarkPending}
                    data-testid="button-topic-bookmark"
                  >
                    {isTopicBookmarked
                      ? isKorean ? "저장됨" : "Saved"
                      : isKorean ? "저장하기" : "Save"}
                    {isTopicBookmarked ? (
                      <BookmarkCheck className="h-4 w-4 fill-current" />
                    ) : (
                      <Bookmark className="h-4 w-4" />
                    )}
                  </Button>
                </CardContent>
              </Card>

              {topic.expertCategories.length > 0 && (
                <Card className="rounded-lg">
                  <CardContent className="p-4">
                    <h2 className="text-sm font-semibold text-foreground">
                      {isKorean ? "도움을 받을 수 있는 분야" : "Where to Find Help"}
                    </h2>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {topic.expertCategories.map((expertCategory) => (
                        <Badge
                          key={expertCategory}
                          variant="secondary"
                          className="rounded-md"
                        >
                          {expertCategory.replaceAll("_", " ")}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </aside>
          </div>
        </section>
      </main>
    </>
  );
}
